'use client';
import { Suspense, useEffect, useState } from 'react';
import { motion } from "framer-motion";
import GeminiAvatar from "@/agents/gemini-live/components/GeminiAvatar";
import { I18nProvider } from "@/contexts/I18nProvider";
import generalAgentService, { GeneralAgentConfig } from './services/generalAgentService';
import type { GeminiTool } from '@/agents/gemini-live/lib/tools';

interface GeneralAgentProps {
    isOpen: boolean;
    onClose: () => void;
    isMapMode?: boolean;
    /** When true, the agent runs in Hub mode with proxy tools for iframe interaction */
    isHubMode?: boolean;
    /** Hub-level tools registry (scene, model, tour, IoT, etc.) */
    hubTools?: Record<string, GeminiTool>;
    /** System instruction override for Hub mode */
    hubSystemInstruction?: string;
    /** Custom opening greeting message for Hub mode */
    hubOpeningMessage?: string;
}

const GeneralAgentContent = ({
    isOpen, onClose, isMapMode = false,
    isHubMode = false, hubTools, hubSystemInstruction, hubOpeningMessage,
}: {
    isOpen: boolean;
    onClose: () => void;
    isMapMode?: boolean;
    isHubMode?: boolean;
    hubTools?: Record<string, GeminiTool>;
    hubSystemInstruction?: string;
    hubOpeningMessage?: string;
}) => {
    const [config, setConfig] = useState<GeneralAgentConfig | null>(null);

    // Reset window.id_path on mount to ensure the agent runs in General Portal mode
    useEffect(() => {
        window.id_path = undefined;
        generalAgentService.getGlobalConfig().then(setConfig);
    }, []);

    const handleClose = () => {
        onClose();    // Đóng UI (set isOpen = false)
    };

    return (
        <div className="flex-1 relative pointer-events-auto w-full h-full">
            {config ? (
                <GeminiAvatar 
                    isOpen={isOpen}
                    isHubMode={isHubMode}
                    hubTools={hubTools}
                    hubSystemInstruction={hubSystemInstruction}
                    hubOpeningMessage={hubOpeningMessage}
                    modelPath={config.modelPath}
                    animationPath={config.animationPath}
                    skinnedMesh={config.skinnedMesh}
                    voice={config.voiceType}
                    isMapMode={isMapMode}
                    onClose={handleClose}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold animate-pulse">⏳</span>
                </div>
            )}
        </div>
    );
};

export default function GeneralAgent({
    isOpen, onClose, isMapMode = false,
    isHubMode = false, hubTools, hubSystemInstruction, hubOpeningMessage,
}: GeneralAgentProps) {
    // Stay mounted while closed (hidden) so the 3D canvas initialises at page load
    // instead of during the open animation, which flashed/lost the WebGL context.
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: isOpen ? 1 : 0, scale: isOpen ? 1 : 0.85 }}
            transition={{ duration: 0.3 }}
            className={isMapMode
                ? "fixed bottom-[190px] left-4 z-[120] w-[160px] md:w-[200px] h-[320px] md:h-[400px] flex flex-col pointer-events-auto"
                : "fixed bottom-[260px] md:bottom-[200px] right-2 md:right-6 z-[120] w-[160px] md:w-[200px] h-[320px] md:h-[400px] flex flex-col pointer-events-auto"
            }
            style={{
                overflow: 'visible',
                pointerEvents: isOpen ? 'auto' : 'none',
                visibility: isOpen ? 'visible' : 'hidden',
            }}
        >
            <Suspense fallback={null}>
                <I18nProvider>
                    <GeneralAgentContent
                        isOpen={isOpen}
                        onClose={onClose}
                        isMapMode={isMapMode}
                        isHubMode={isHubMode}
                        hubTools={hubTools}
                        hubSystemInstruction={hubSystemInstruction}
                        hubOpeningMessage={hubOpeningMessage}
                    />
                </I18nProvider>
            </Suspense>
        </motion.div>
    );
}
